import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { CreativeGrouping } from "@nexus/contracts";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { CreativeAdSpendBreakdown } from "@/features/explore/components/CreativeAdSpendBreakdown";
import { formatExactCurrency } from "@/features/dashboard/lib/format";
import type { MyBrandFiltersInput } from "@/lib/queries/dashboard.queries";
import {
  myBrandCreativeAdsQueryOptions,
  type DrilldownPageFiltersInput,
} from "@/lib/queries/drilldown.queries";

interface DrilldownCreativeAdSpendPanelProps {
  hash: string;
  grouping: CreativeGrouping;
  adsCount?: number;
  variant: "collapsible" | "inline";
  myBrandFilters: MyBrandFiltersInput;
  drilldownFilters: DrilldownPageFiltersInput;
  currencySymbol: string;
  enabled?: boolean;
}

export function DrilldownCreativeAdSpendPanel({
  hash,
  grouping,
  adsCount,
  variant,
  myBrandFilters,
  drilldownFilters,
  currencySymbol,
  enabled = true,
}: DrilldownCreativeAdSpendPanelProps) {
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const [expanded, setExpanded] = useState(variant === "inline");
  const queryEnabled = enabled && (variant === "inline" || expanded);
  const query = useQuery(
    myBrandCreativeAdsQueryOptions(
      activeTenantId,
      hash,
      grouping,
      myBrandFilters,
      drilldownFilters,
      queryEnabled,
    ),
  );

  return (
    <CreativeAdSpendBreakdown
      variant={variant}
      adsCount={adsCount}
      expanded={expanded}
      onToggle={() => setExpanded((current) => !current)}
      section={query.data}
      isLoading={queryEnabled && query.isLoading}
      isError={queryEnabled && query.isError}
      onRetry={() => void query.refetch()}
      formatSpend={(spend) => formatExactCurrency(spend, currencySymbol)}
    />
  );
}
